import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { HealthBadge, HealthFilterHeader, calcWeightedHealth } from '../health/index.js';
import { getStatusVariant } from './ChildTicketsModal.js';
import { FilterableHeader, ProgressFilterHeader } from './FilterHeaders.js';
import { MemberRoleBadges, type MemberRole } from './MemberRoleBadges.js';
import type { Project, IntegratedProduct } from '../../../types/project.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクト進捗率に応じたプログレスバー色（0-40%水色、40-90%黄色、90%+緑）を決定
 * @graph-connects none
 */
function getProgressColor(ratio: number | null): string {
  if (ratio === null) return 'var(--color-border)';
  if (ratio < 40) return '#38bdf8'; // 水色 (sky-400)
  if (ratio < 90) return '#facc15'; // 黄色 (yellow-400)
  return '#22c55e'; // 緑 (green-500)
}

/**
 * 進捗率バー
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクト進捗率を色付きバーとパーセンテージで視覚表示
 * @graph-connects none
 */
export function ProgressBar({ ratio }: { ratio: number | null }) {
  const pct = ratio ?? 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-4 bg-[var(--color-bg-base)] rounded-full overflow-hidden min-w-[80px]">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${String(Math.min(pct, 100))}%`,
            backgroundColor: getProgressColor(ratio),
          }}
        />
      </div>
      <span className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap w-12 text-right">
        {ratio !== null ? `${ratio.toFixed(1)}%` : '-'}
      </span>
    </div>
  );
}

/**
 * 統合プロダクト展開行
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business Gen1統合プロダクトの折りたたみ可能な行。配下Gen2チャンクプロジェクトの進捗・ステータスを展開表示
 * @graph-connects none
 */
function IntegratedProductRow({
  ip,
  onProjectClick,
}: {
  ip: IntegratedProduct;
  onProjectClick: (p: Project) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // 統合プロダクト配下のchunk親の集計
  const totalActual = ip.chunkProjects.reduce((s, p) => s + p.totalActualPoint, 0);
  const totalPlanned = ip.chunkProjects.reduce((s, p) => s + p.totalPlannedPoint, 0);
  const aggRatio = totalPlanned > 0 ? Math.round((totalActual / totalPlanned) * 1000) / 10 : null;
  const aggHealth = calcWeightedHealth(ip.chunkProjects);

  return (
    <>
      <tr
        className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors"
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <td className="py-3 px-4 text-sm text-[var(--color-accent-primary)] whitespace-nowrap">
          <span className="mr-1">{expanded ? '▼' : '▶'}</span>
          <Link
            to="/projects/$pjKey"
            params={{ pjKey: ip.issueKey }}
            className="hover:underline"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {ip.issueKey}
          </Link>
        </td>
        <td
          className="py-3 px-4 text-sm text-[var(--color-text-primary)] font-medium truncate"
          title={ip.summary}
        >
          {ip.summary}
        </td>
        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">-</td>
        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">-</td>
        <td className="py-3 px-4 text-sm">
          <ProgressBar ratio={aggRatio} />
        </td>
        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] whitespace-nowrap">
          {totalActual}/{totalPlanned}
        </td>
        <td className="py-3 px-4 text-sm text-center">
          <HealthBadge health={aggHealth} />
        </td>
        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">-</td>
        <td className="py-3 px-4 text-sm">
          <Badge variant={getStatusVariant(ip.status)} size="sm">
            {ip.status}
          </Badge>
        </td>
      </tr>
      {expanded &&
        ip.chunkProjects.map((p) => (
          <ProjectTableRow key={p.id} project={p} indent onProjectClick={onProjectClick} />
        ))}
    </>
  );
}

/**
 * プロジェクト行
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business Gen2プロジェクトの1行表示。issueKey・担当者・チーム・進捗率・子チケット数を表示しクリックで子チケットモーダルを開く。role 指定時は案件名の次にロール（開発者/レビュアー/開発リーダー）列を追加する（チームビュー用）
 * @graph-connects MemberRoleBadges [calls] 担当ロールバッジ描画（role 指定時のみ）
 */
export function ProjectTableRow({
  project,
  indent = false,
  onProjectClick,
  role,
}: {
  project: Project;
  indent?: boolean;
  onProjectClick: (p: Project) => void;
  /**
   * この行のメンバー担当ロール。指定時のみ案件名の右にロール列を 1 つ追加する。
   * 列数が変わるため、呼び出し側のヘッダー / colgroup も合わせること（チームビューのみ指定）。
   */
  role?: MemberRole;
}) {
  return (
    <tr
      className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors"
      onClick={() => {
        onProjectClick(project);
      }}
    >
      <td
        className={`py-3 px-4 text-sm text-[var(--color-accent-primary)] whitespace-nowrap ${indent ? 'pl-10' : ''}`}
      >
        <Link
          to="/projects/$pjKey"
          params={{ pjKey: project.issueKey }}
          className="hover:underline"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {project.issueKey}
        </Link>
        <Link
          to="/projects/$pjKey/spec"
          params={{ pjKey: project.issueKey }}
          className="block text-xs text-[var(--color-text-secondary)] hover:underline"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          要件定義
        </Link>
      </td>
      <td
        className="py-3 px-4 text-sm text-[var(--color-text-primary)] truncate"
        title={project.summary}
      >
        {project.summary}
      </td>
      {role && (
        <td className="py-3 px-4 text-sm">
          <MemberRoleBadges role={role} />
        </td>
      )}
      <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">
        {project.responsibleTeam ?? '-'}
      </td>
      <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">
        {project.assignee ?? '-'}
      </td>
      <td className="py-3 px-4 text-sm">
        <ProgressBar ratio={project.completionRatio} />
      </td>
      <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] whitespace-nowrap">
        {project.totalActualPoint}/{project.totalPlannedPoint}
      </td>
      <td className="py-3 px-4 text-sm text-center">
        <HealthBadge health={project.health} />
      </td>
      <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">
        {project.devLeader ?? '-'}
      </td>
      <td className="py-3 px-4 text-sm">
        <Badge variant={getStatusVariant(project.status)} size="sm">
          {project.status}
        </Badge>
      </td>
    </tr>
  );
}

/**
 * プロジェクト一覧テーブル（共通）
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 統合プロダクト+スタンドアロンプロジェクトの統合テーブル。チーム・ステータス・進捗率フィルタとソート付き
 * @graph-connects none
 */
export function ProjectTable({
  integratedProducts,
  standaloneProjects,
  onProjectClick,
  onHealthInfoClick,
  emptyMessage,
  filters,
}: {
  integratedProducts: IntegratedProduct[];
  standaloneProjects: Project[];
  onProjectClick: (p: Project) => void;
  onHealthInfoClick: () => void;
  emptyMessage: string;
  filters?: {
    filterTeam: string;
    setFilterTeam: (v: string) => void;
    filterAssignee: string;
    setFilterAssignee: (v: string) => void;
    filterProgress: string;
    setFilterProgress: (v: string) => void;
    filterDevLeader: string;
    setFilterDevLeader: (v: string) => void;
    filterStatus: string;
    setFilterStatus: (v: string) => void;
    filterHealth: string;
    setFilterHealth: (v: string) => void;
    options: { teams: string[]; assignees: string[]; devLeaders: string[]; statuses: string[] };
    /**
     * チーム filter のオプション色（任意）。指定時は責任チームのドロップダウンに
     * 色付きチップを表示する（横断 PMO 用）。
     */
    getTeamOptionColor?: (opt: string) => { bg: string; text: string } | undefined;
    /** true の場合、担当チーム filter のドロップダウンを開けないように固定する（team_leader 用）。 */
    teamFilterLocked?: boolean;
  };
}) {
  const { t } = useTranslation();

  if (integratedProducts.length === 0 && standaloneProjects.length === 0) {
    return <p className="text-center py-8 text-[var(--color-text-muted)]">{emptyMessage}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[140px]" />
          <col className="w-[300px]" />
          <col className="w-[100px]" />
          <col className="w-[90px]" />
          <col className="w-[180px]" />
          <col className="w-[80px]" />
          <col className="w-[70px]" />
          <col className="w-[90px]" />
          <col className="w-[90px]" />
        </colgroup>
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.issueKey')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.summary')}
            </th>
            {filters ? (
              <FilterableHeader
                label={t('projects.fields.responsibleTeam')}
                value={filters.filterTeam}
                options={filters.options.teams}
                onChange={filters.setFilterTeam}
                getOptionColor={filters.getTeamOptionColor}
                locked={filters.teamFilterLocked}
              />
            ) : (
              <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                {t('projects.fields.responsibleTeam')}
              </th>
            )}
            {filters ? (
              <FilterableHeader
                label={t('projects.fields.pl')}
                value={filters.filterAssignee}
                options={filters.options.assignees}
                onChange={filters.setFilterAssignee}
              />
            ) : (
              <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                {t('projects.fields.pl')}
              </th>
            )}
            {filters ? (
              <ProgressFilterHeader
                label={t('projects.fields.completionRatio')}
                value={filters.filterProgress}
                onChange={filters.setFilterProgress}
              />
            ) : (
              <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                {t('projects.fields.completionRatio')}
              </th>
            )}
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.points')}
            </th>
            {filters ? (
              <HealthFilterHeader
                value={filters.filterHealth}
                onChange={filters.setFilterHealth}
                onInfoClick={onHealthInfoClick}
              />
            ) : (
              <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                <span className="inline-flex items-center gap-1">
                  {t('projects.health.title')}
                  <button
                    type="button"
                    onClick={onHealthInfoClick}
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[var(--color-text-muted)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-text-primary)] transition-colors text-[10px] leading-none"
                    aria-label={t('projects.health.title')}
                  >
                    ?
                  </button>
                </span>
              </th>
            )}
            {filters ? (
              <FilterableHeader
                label={t('projects.fields.devLeader')}
                value={filters.filterDevLeader}
                options={filters.options.devLeaders}
                onChange={filters.setFilterDevLeader}
              />
            ) : (
              <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                {t('projects.fields.devLeader')}
              </th>
            )}
            {filters ? (
              <FilterableHeader
                label={t('projects.fields.status')}
                value={filters.filterStatus}
                options={filters.options.statuses}
                onChange={filters.setFilterStatus}
              />
            ) : (
              <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                {t('projects.fields.status')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {integratedProducts.map((ip) => (
            <IntegratedProductRow key={ip.id} ip={ip} onProjectClick={onProjectClick} />
          ))}
          {standaloneProjects.map((p) => (
            <ProjectTableRow key={p.id} project={p} onProjectClick={onProjectClick} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
